/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { PopupClose } from '../Icon/PopupClose'
import ImageOptimize from '../ImageOptimize'

import {
  AddToCartType,
  CouponTarget,
  Languages,
  VoucherTypes,
} from '@/constants'
import { useAuth, useCart, useStore } from '@/containers'
import type { Cart, VoucherItem } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { convertCoupon } from '@/utils/next-util'
import { Listbox } from '@headlessui/react'
import clsx from 'clsx'
import { getCookie } from 'cookies-next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { FormButton } from '../Form'
import { Loading } from '../Loading/Loading'
import PopupBody from './PopupBody'
import PopupFrame from './PopupFrame'
import PopupTitle from './PopupTitle'

interface ModalVoucherList {
  showListCoupons: boolean
  setShowListCoupons: Dispatch<SetStateAction<boolean>>
  shippingMethod?: string
}
const ModalVoucherList = ({
  showListCoupons,
  setShowListCoupons,
  shippingMethod,
}: ModalVoucherList) => {
  const t = useTranslations('Cart')
  const router = useRouter()
  const tCommon = useTranslations('Common')
  const { getCartCoupons, cartCoupons, setCouponApply, couponApply } = useCart()
  const { selectedLang } = useStore()
  const {
    fetchCart,
    setErrorAuth,
    errorAuth,
    cart,
    setVoucherApply,
    voucherApply,
  } = useAuth()
  const [couponSelected, setCouponSelected] = useState<string[]>(
    (voucherApply.length > 0 && voucherApply) || couponApply || []
  )
  const [inputVoucher, setInputVoucher] = useState('')
  const [isApply, setIsApply] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    getCartCoupons()
    setInputVoucher('')
    setIsApply(false)
    setErrorAuth({
      status: false,
      message: '',
      key: '',
    })
  }, [])

  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'USD'
      default:
        return 'VND'
    }
  }

  function checkVoucherType(voucher: VoucherItem) {
    switch (voucher.type) {
      case VoucherTypes.PERCENT:
        return voucher.percentValue + '%'
      case VoucherTypes.FIXED:
        return voucher.fixedValue
          ?.toLocaleString(selectedLang as string, {
            style: 'currency',
            currency: currency(selectedLang as string),
          })
          .replace(/\s₫/g, 'đ')
      default:
        break
    }
  }

  const updateVoucher = (code: string[], manually = false) => {
    fetchCart({
      coupon: code,
      shippingMethod,
      action: 'popup discount 1',
    })
      .then((res: Cart) => {
        setIsProcessing(false)
        setErrorAuth({
          status: false,
          message: '',
          key: '',
        })
        if (res.coupons.filter(e => e.mes).length > 0) {
          if (manually && res.coupons.length > 1) {
            setShowListCoupons(false)
            setInputVoucher('')
          }
          const couponApplied = res.coupons
            .filter(e => !e.mes)
            .map(e => e.coupon.initialKey) as string[]
          setCouponSelected(couponApplied)
          setCouponApply(couponApplied)
        }
        setShowListCoupons(false)
        getCartCoupons()
        toast.success(
          getCookie('language') === Languages.EN
            ? 'Apply promotional code successfully!'
            : 'Áp dụng mã khuyến mại thành công!',
          {
            position: 'bottom-center',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        )
      })
      .catch(() => {
        setIsProcessing(false)
        setInputVoucher('')
        manually && setShowListCoupons(false)
      })
  }

  const handleChange = (event: {
    target: { value: SetStateAction<string> }
  }) => {
    setInputVoucher(event.target.value)
    setCouponApply([event.target.value as string])
    if (event.target.value) {
      setIsApply(true)
    }
  }

  const applyCoupon = async () => {
    setIsProcessing(true)
    setCouponApply(couponSelected)
    await updateVoucher(couponSelected, true)
  }

  const clearCoupon = () => {
    setCouponApply([])
    setInputVoucher('')
    setVoucherApply([])
    fetchCart({
      shippingMethod,
      ...(router?.query?.type === AddToCartType.PRE_ORDER &&
        router?.query?.id && {
          productIds: router?.query?.id as string | undefined,
        }),
      ...(router?.query?.type === AddToCartType.INSTALLMENT &&
        router?.query?.id && {
          productIds: (router?.query?.id + ':1') as string | undefined,
        }),
      action: 'form discount 2',
    })
    setIsApply(false)
    setErrorAuth({
      status: false,
      message: '',
      key: '',
    })
  }

  const cardCouponsMapping = useMemo(() => {
    if (!cartCoupons?.length) return []
    return cartCoupons.reduce((acc: any, cur: VoucherItem) => {
      if (cur.target === CouponTarget.PRODUCT) {
        const productGroup = acc.find((e: any) => e._id === cur.product?._id)
        const couponApply = cart?.coupons?.find(
          coupon =>
            coupon.coupon?.initialKey ===
            convertCoupon(cur.code, cur.product?._id)
        )
        if (productGroup) {
          productGroup.coupons.push({
            ...cur,
            ...(couponApply && {
              mes: couponApply.mes,
            }),
          })
          return acc
        }
        return [
          ...acc,
          {
            ...cur.product,
            coupons: [
              {
                ...cur,
                ...(couponApply && {
                  mes: couponApply.mes,
                }),
              },
            ],
          },
        ]
      }

      const couponApply = cart?.coupons?.find(
        coupon => coupon.coupon?.initialKey === `${cur.code}_orderCoupon`
      )
      return [
        ...acc,
        {
          name: {
            vi: 'Áp dụng cho đơn hàng',
          },
          _id: 'orderCoupon',
          coupons: [
            {
              ...cur,
              ...(couponApply && {
                mes: couponApply.mes,
              }),
            },
          ],
        },
      ]
    }, [] as any)
  }, [cartCoupons, cart])

  const couponOnchange = (value: string[]) => {
    const couponErrors = cart.coupons
      ?.filter(e => e.mes)
      ?.map(e => e.coupon?.initialKey)
    const changed =
      value.find(item => !couponSelected.includes(item)) ||
      couponSelected.find(item => !value.includes(item))
    const data = value
      .map(e => {
        const [couponCode, productId] = changed?.split('_') as any[]
        if (!e.includes(couponCode) && e.includes(productId)) {
          return null
        }
        return e
      })
      .filter(Boolean)
      .filter(e => {
        if (couponErrors?.length > 0) {
          return !couponErrors.includes(e as string)
        }
        return true
      })
    setCouponSelected(data as string[])
  }

  const popupClose = () => {
    setShowListCoupons(false)
    setInputVoucher('')
    getCartCoupons()
  }
  return (
    <PopupFrame
      open={showListCoupons}
      onClose={() => popupClose()}
      closeText={tCommon('close')}
      type='voucher'
      className='min-h-[480px] w-full max-w-[560px] bg-white-400'
    >
      <PopupTitle
        className='border-b border-zinc-300 px-3 pt-[20px]'
        type={'voucher'}
      >
        {
          <div className='relative flex w-full flex-wrap items-center'>
            <p className='w-[70%] text-lg font-bold'>{t('voucherForYou')}</p>
            <div
              onClick={() => setShowListCoupons(false)}
              className='absolute right-0 top-[50%] z-10 flex translate-y-[-50%] cursor-pointer items-center gap-1'
            >
              <PopupClose className=' text-dark-400' />
              {tCommon('close')}
            </div>
          </div>
        }
      </PopupTitle>
      <PopupBody>
        <div className='listCoupons mt-[18px] flex flex-wrap justify-between gap-y-[12px] border-grey-100 scrollbar-hide'>
          <div className='flex w-full flex-wrap items-center justify-between gap-3 px-3'>
            <div className='inputGroup flex-auto relative flex-1 '>
              <input
                placeholder='Nhập mã giảm giá'
                type='text'
                name='formDiscount'
                value={inputVoucher}
                onChange={handleChange}
                className={clsx(
                  errorAuth && 'border-red',
                  ' h-[48px] w-full rounded-[6px] border border-grey p-3 text-base text-fieldInput font-bold text-blue-800 shadow-none placeholder:font-normal placeholder:text-dark-400/70 focus:border-grey focus:shadow-none focus:outline-none focus:ring-0'
                )}
              />
              {inputVoucher ? (
                <button
                  className='clear absolute right-3 top-[50%] z-10 translate-y-[-50%]'
                  onClick={() => clearCoupon()}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={20}
                    height={20}
                    viewBox='0 0 20 20'
                    fill='none'
                  >
                    <circle cx={10} cy={10} r='9.5' fill='#B1B1B1' />
                    <path
                      d='M14.0716 12.7695C14.4319 13.1299 14.4319 13.7123 14.0716 14.0728C13.8919 14.2525 13.656 14.3428 13.4199 14.3428C13.184 14.3428 12.9481 14.2525 12.7683 14.0728L9.99902 11.3032L7.2297 14.0728C7.04998 14.2525 6.81407 14.3428 6.57816 14.3428C6.34208 14.3428 6.10617 14.2525 5.92646 14.0728C5.56618 13.7123 5.56618 13.1299 5.92646 12.7695L8.69595 10.0001L5.92646 7.23071C5.56618 6.87026 5.56618 6.28789 5.92646 5.92744C6.2869 5.56716 6.86925 5.56716 7.2297 5.92744L9.99902 8.697L12.7683 5.92744C13.1288 5.56716 13.7111 5.56716 14.0716 5.92744C14.4319 6.28789 14.4319 6.87026 14.0716 7.23071L11.3021 10.0001L14.0716 12.7695Z'
                      fill='white'
                    />
                  </svg>
                </button>
              ) : (
                ''
              )}
            </div>
            <button
              className='h-[48px] w-[114px] rounded bg-red px-[10px] py-0 text-fieldInput font-bold text-white disabled:bg-grey-1400 disabled:outline-none'
              disabled={!isApply}
              type='button'
              onClick={() => updateVoucher([inputVoucher], true)}
            >
              Áp dụng
            </button>
          </div>
          {inputVoucher &&
            cart?.coupons?.length <= 1 &&
            cart?.coupons?.filter(e => e.mes).length > 0 && (
              <div className='mb-1 ml-3 w-full text-mainBody font-normal leading-tight text-red opacity-70'>
                {cart.coupons
                  .filter(e => e.mes)
                  .map((e, i) => (
                    <span key={`coupon-${i}`}>
                      {e.coupon.couponCode} - {e.mes}
                    </span>
                  ))}
              </div>
            )}
          <p className='mb-3 px-3 text-base font-normal text-neutral-600'>
            {t('voucherSubTitle')}
          </p>

          {cardCouponsMapping && cardCouponsMapping.length > 0 ? (
            <div className='main h-full max-h-[320px] w-full overflow-y-auto pl-3 pr-2'>
              <Listbox
                value={couponSelected}
                onChange={async (e: string[]) => couponOnchange(e)}
                multiple
              >
                <Listbox.Options
                  as='div'
                  static
                  className={`flex w-full cursor-pointer flex-wrap items-start justify-start overflow-auto rounded-md bg-white text-base`}
                >
                  <div>
                    {cardCouponsMapping.map((e: any) => (
                      <div key={e._id}>
                        <p className={'mb-3 text-Headline text-dark-500'}>
                          {e._id === 'orderCoupon' ? (
                            <strong>Áp dụng cho đơn hàng</strong>
                          ) : (
                            <>
                              <strong>Sản phẩm áp dụng: </strong>{' '}
                              {getTranslate(e.name)}
                            </>
                          )}
                        </p>
                        <div className='ml-[40px] flex flex-wrap gap-2 pb-2'>
                          {e.coupons &&
                            e.coupons.map((c: VoucherItem) => (
                              <Listbox.Option
                                as='div'
                                key={c._id}
                                className={({ selected }) =>
                                  clsx(
                                    `middle relative mr-1 w-full cursor-pointer text-fieldInput before:absolute before:left-[-40px] before:top-1/2 before:-translate-y-1/2 before:transform`,
                                    c.mes
                                      ? `!text-yellow-500 before:content-[url('/assets/icons/couponWarning.svg')]`
                                      : selected
                                      ? `before:content-[url('/assets/icons/couponChecked.svg')]`
                                      : `before:content-[url('/assets/icons/couponPending.svg')]`
                                  )
                                }
                                value={convertCoupon(c.code, e._id)}
                              >
                                <div
                                  className={clsx(
                                    'voucherItem flex h-20 w-full items-center overflow-hidden rounded-lg bg-red-600 bg-opacity-5',
                                    c.mes && '!bg-yellow-500/10'
                                  )}
                                >
                                  <div className='voucherLeft min-w-[120px] max-w-[30%] flex-[30%]'>
                                    <div className='flex w-full flex-wrap justify-center'>
                                      <p
                                        className={clsx(
                                          'w-full text-center text-base text-red-500',
                                          c.mes && '!text-yellow-500'
                                        )}
                                      >
                                        Giảm
                                      </p>
                                      <p
                                        className={clsx(
                                          'w-full text-center text-xl font-extrabold text-red-500',
                                          c.mes && '!text-yellow-500'
                                        )}
                                      >
                                        {checkVoucherType(c)}
                                      </p>
                                      {c.percentValue && c.fixedValue && (
                                        <i
                                          className={clsx(
                                            'text-Voucher text-price',
                                            c.mes && '!text-yellow-500'
                                          )}
                                        >
                                          (~
                                          {c.fixedValue
                                            .toLocaleString(Languages.VN, {
                                              style: 'currency',
                                              currency: 'VND',
                                            })
                                            .replace(/\s₫/g, 'đ')}
                                          )
                                        </i>
                                      )}
                                    </div>
                                  </div>
                                  <div className='voucherRight max-w-[70%] flex-[70%]'>
                                    <div className='flex w-full flex-wrap items-center pl-3 leading-4'>
                                      <p
                                        className={clsx(
                                          'mb-1.5 w-full text-sm font-bold text-red-500',
                                          c.mes && '!text-yellow-500'
                                        )}
                                      >
                                        {dayjs(c.startTime).format(
                                          'HH:mm DD.MM'
                                        )}{' '}
                                        ~{' '}
                                        {dayjs(c.endTime).format('HH:mm DD.MM')}
                                      </p>
                                      <div
                                        className={clsx(
                                          'mb-0 w-full text-xs font-normal text-red-500',
                                          c.mes && '!text-yellow-500'
                                        )}
                                      >
                                        {getTranslate(c.description)}
                                      </div>
                                      {c.minValue ? (
                                        <div
                                          className={clsx(
                                            'flex text-xs font-normal text-price',
                                            c.mes && '!text-yellow-500'
                                          )}
                                        >
                                          <span>{t('minValueLabel')}</span>{' '}
                                          <span className='ml-[4px] font-bold'>
                                            {c.minValue
                                              .toLocaleString(
                                                selectedLang as string,
                                                {
                                                  style: 'currency',
                                                  currency: currency(
                                                    selectedLang as string
                                                  ),
                                                }
                                              )
                                              .replace(/\s₫/g, 'đ')}
                                          </span>
                                        </div>
                                      ) : (
                                        ''
                                      )}
                                      {c.maxDiscountValue &&
                                        c.type !== VoucherTypes.FIXED && (
                                          <div
                                            className={clsx(
                                              'flex text-xs font-normal text-price',
                                              c.mes && '!text-yellow-500'
                                            )}
                                          >
                                            <span>{t('maxDiscountValue')}</span>{' '}
                                            <span className='ml-[4px] font-bold'>
                                              {c.maxDiscountValue
                                                .toLocaleString(
                                                  selectedLang as string,
                                                  {
                                                    style: 'currency',
                                                    currency: currency(
                                                      selectedLang as string
                                                    ),
                                                  }
                                                )
                                                .replace(/\s₫/g, 'đ')}
                                            </span>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                                {c.mes && <span>{c.mes}</span>}
                              </Listbox.Option>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </Listbox.Options>
              </Listbox>
            </div>
          ) : (
            <div className='emptyVoucher flex w-full flex-wrap justify-center'>
              <ImageOptimize
                src={'../empty-image.png'}
                width={240}
                height={240}
                alt='Empty'
              />
              <div className='w-full text-center text-base font-normal leading-7 text-neutral-600'>
                Chưa có mã giảm giá phù hợp
              </div>
            </div>
          )}

          <div className='block w-full px-3'>
            <FormButton
              className={`shadow-top mx-auto mb-2 mt-10 h-[48px] w-full overflow-hidden rounded bg-red-1000 text-fieldLabel font-bold uppercase text-white outline-0`}
              disabled={isProcessing}
              onClick={() => applyCoupon()}
            >
              {isProcessing && <Loading />} Xác nhận
            </FormButton>
          </div>
        </div>
      </PopupBody>
    </PopupFrame>
  )
}

export default ModalVoucherList
